import React from "react";
import Logo from "../../assets/images/logoCube.png";

/**
 * Premium ProntoPsy call & route transition loader.
 * Displays a branded loading state instead of a blank white screen
 * when joining or closing audio, video, and chat consultations.
 */
const CallTransitionLoader = ({
  title = "Connecting to Consultation…",
  subtitle = "Setting up your secure consultation room. Please hold on…",
  fullScreen = true,
  dark = true,
  className = "",
}) => {
  return (
    <div
      className={`${
        fullScreen
          ? "fixed inset-0 z-[100000] w-screen h-screen"
          : "w-full h-full min-h-[16rem]"
      } flex flex-col items-center justify-center ${
        dark
          ? "bg-gradient-to-b from-[#0a1128] via-[#001f4d] to-[#0a1128] text-white"
          : "bg-gradient-to-b from-slate-50 via-white to-slate-100 text-slate-800"
      } p-6 select-none animate-fadeIn ${className}`}
      role="status"
      aria-live="polite">
      
      {/* Ambient glowing backdrop */}
      <div
        className={`absolute w-80 h-80 rounded-full blur-3xl pointer-events-none -z-10 animate-pulse ${
          dark
            ? "bg-gradient-to-tr from-[#002C6B]/50 via-[#b32cbd]/30 to-cyan-500/20"
            : "bg-gradient-to-tr from-[#002C6B]/15 via-[#b32cbd]/10 to-transparent"
        }`}
      />

      {/* Main card */}
      <div className="flex flex-col items-center max-w-md w-full text-center">
        {/* Animated Brand Logo Container */}
        <div className="relative mb-6 flex items-center justify-center">
          {/* Rotating halo rings */}
          <div
            className={`absolute -inset-3.5 rounded-full border-2 border-dashed ${
              dark ? "border-cyan-400/40" : "border-[#002C6B]/30"
            } animate-spin`}
            style={{ animationDuration: "7s" }}
          />
          <div
            className={`absolute -inset-1.5 rounded-full border-2 ${
              dark ? "border-[#b32cbd]/60" : "border-[#b32cbd]/40"
            } animate-spin`}
            style={{ animationDuration: "3.5s", animationDirection: "reverse" }}
          />

          <div
            className={`w-20 h-20 rounded-2xl p-3 shadow-2xl flex items-center justify-center ${
              dark
                ? "bg-white/10 backdrop-blur-md border border-white/20"
                : "bg-white shadow-lg border border-slate-200"
            }`}>
            <img
              src={Logo}
              alt="ProntoPsy Logo"
              className="w-full h-full object-contain animate-pulse"
            />
          </div>
        </div>

        {/* Title */}
        <h2
          className={`text-xl md:text-2xl font-bold tracking-tight mb-2 font-RobotoSlab ${
            dark ? "text-white" : "text-[#002C6B]"
          }`}>
          {title}
        </h2>

        {/* Subtitle */}
        {subtitle && (
          <p
            className={`text-sm md:text-base leading-relaxed mb-6 max-w-sm ${
              dark ? "text-slate-300/85" : "text-slate-600"
            }`}>
            {subtitle}
          </p>
        )}

        {/* Animated Progress Bar */}
        <div
          className={`w-52 h-1.5 rounded-full overflow-hidden relative ${
            dark ? "bg-white/15" : "bg-slate-200"
          }`}>
          <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#002C6B] via-cyan-400 to-[#b32cbd] w-1/2 rounded-full animate-loadingBar" />
        </div>
      </div>
    </div>
  );
};

export default CallTransitionLoader;
